import { cn } from "@/lib/cn";
import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";
import { type ReactNode, useCallback, useState } from "react";

export type SortDirection = "asc" | "desc" | null;

export interface DataTableColumn<T> {
  /** Unique key identifying this column */
  key: string;
  /** Column header label */
  label: string;
  /** Whether this column is sortable */
  sortable?: boolean;
  /** CSS class variant for the td cell: "muted", "mono", "num" */
  cellVariant?: "muted" | "mono" | "num";
  /** Custom render function for cell content */
  render?: (row: T, index: number) => ReactNode;
  /** Header text alignment */
  headerAlign?: "left" | "right" | "center";
  /**
   * IR-1228 (Rotem c61856: "platform - same width as status column"): fix this
   * column's width. Set on the `th` only — `border-collapse` makes the header
   * cell govern the whole column, so the body cells need nothing.
   */
  width?: number | string;
  /**
   * IR-1697 r11: a floor for the column. `width` is only a hint under
   * `table-layout: auto` and the Groups Ingestion column was squeezed to one
   * letter; `min-width` on the `th` is what the browser will not go below.
   */
  minWidth?: number | string;
  /**
   * Extra class for BOTH the th and the td of this column. Exists for
   * `iris-td--sticky-actions` (IR-1351), which has to sit on the header cell
   * too or the pinned column's header scrolls away from its own body.
   */
  cellClassName?: string;
}

export interface DataTableProps<T> {
  /** Column definitions */
  columns: DataTableColumn<T>[];
  /** Row data */
  data: T[];
  /** Unique key extractor for each row */
  rowKey: (row: T, index: number) => string | number;
  /** Called when a sortable column header is clicked */
  onSort?: (columnKey: string, direction: SortDirection) => void;
  /** Currently sorted column key */
  sortKey?: string | null;
  /** Current sort direction */
  sortDirection?: SortDirection;
  /** Called when a row is clicked */
  onRowClick?: (row: T, index: number) => void;
  /** Optional empty state message */
  emptyMessage?: string;
  /**
   * IR-1227 (Rotem 53972): pin the header row so it stays visible while the
   * table body scrolls. Opt-in — the consumer must also give the table a
   * bounded, scrollable ancestor (e.g. `maxHeight` + `overflowY: auto`) for
   * `position: sticky` to have somewhere to stick.
   */
  stickyHeader?: boolean;
  className?: string;
}

/**
 * Data table using iris-table CSS classes with sortable column headers.
 * Supports cell variants (muted, mono, num) via iris-td-- classes.
 */
export function DataTable<T>({
  columns,
  data,
  rowKey,
  onSort,
  onRowClick,
  sortKey: controlledSortKey,
  sortDirection: controlledSortDirection,
  emptyMessage = "No data",
  stickyHeader = false,
  className,
}: DataTableProps<T>) {
  const [internalSortKey, setInternalSortKey] = useState<string | null>(null);
  const [internalSortDir, setInternalSortDir] = useState<SortDirection>(null);

  const sortKey = controlledSortKey ?? internalSortKey;
  const sortDirection = controlledSortDirection ?? internalSortDir;

  const handleSort = useCallback(
    (colKey: string) => {
      let nextDir: SortDirection;
      if (sortKey !== colKey) {
        nextDir = "asc";
      } else if (sortDirection === "asc") {
        nextDir = "desc";
      } else {
        nextDir = null;
      }

      if (!controlledSortKey) {
        setInternalSortKey(nextDir ? colKey : null);
        setInternalSortDir(nextDir);
      }

      onSort?.(colKey, nextDir);
    },
    [sortKey, sortDirection, controlledSortKey, onSort],
  );

  const SortIcon = ({
    colKey,
  }: {
    colKey: string;
  }) => {
    if (sortKey !== colKey) {
      return <ArrowUpDown size={12} style={{ color: "var(--iris-fg-5)", marginLeft: 4 }} />;
    }
    if (sortDirection === "asc") {
      return <ArrowUp size={12} style={{ color: "var(--iris-neon-cyan)", marginLeft: 4 }} />;
    }
    return <ArrowDown size={12} style={{ color: "var(--iris-neon-cyan)", marginLeft: 4 }} />;
  };

  return (
    <table className={cn("iris-table", className)}>
      <thead>
        <tr>
          {columns.map((col) => (
            <th
              key={col.key}
              className={col.cellClassName}
              style={{
                cursor: col.sortable ? "pointer" : undefined,
                textAlign: col.headerAlign ?? "left",
                width: col.width,
                minWidth: col.minWidth,
                userSelect: col.sortable ? "none" : undefined,
                // IR-1227 (Rotem 53972): pin the header to the top of the
                // scroll container. Needs its own background so body rows don't
                // show through, and an inset shadow to keep the bottom rule
                // (border-collapse drops the real border once the row sticks).
                ...(stickyHeader
                  ? {
                      position: "sticky" as const,
                      top: 0,
                      zIndex: 1,
                      background: "var(--iris-bg-2)",
                      boxShadow: "inset 0 -1px 0 var(--iris-line-2)",
                    }
                  : {}),
              }}
              onClick={col.sortable ? () => handleSort(col.key) : undefined}
              onKeyDown={
                col.sortable
                  ? (e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleSort(col.key);
                      }
                    }
                  : undefined
              }
              tabIndex={col.sortable ? 0 : undefined}
              role={col.sortable ? "button" : undefined}
            >
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                }}
              >
                {col.label}
                {col.sortable && <SortIcon colKey={col.key} />}
              </span>
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {data.length === 0 ? (
          <tr>
            <td
              colSpan={columns.length}
              style={{
                textAlign: "center",
                color: "var(--iris-fg-4)",
                padding: "24px 12px",
              }}
            >
              {emptyMessage}
            </td>
          </tr>
        ) : (
          data.map((row, idx) => (
            <tr
              key={rowKey(row, idx)}
              style={onRowClick ? { cursor: "pointer" } : undefined}
              onClick={onRowClick ? () => onRowClick(row, idx) : undefined}
              onKeyDown={
                onRowClick
                  ? (e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onRowClick(row, idx);
                      }
                    }
                  : undefined
              }
              tabIndex={onRowClick ? 0 : undefined}
              role={onRowClick ? "button" : undefined}
            >
              {columns.map((col) => {
                const variantClass = col.cellVariant ? `iris-td--${col.cellVariant}` : undefined;
                return (
                  <td key={col.key} className={cn(variantClass, col.cellClassName)}>
                    {col.render
                      ? col.render(row, idx)
                      : (row as Record<string, unknown>)[col.key] != null
                        ? String((row as Record<string, unknown>)[col.key])
                        : ""}
                  </td>
                );
              })}
            </tr>
          ))
        )}
      </tbody>
    </table>
  );
}
